import { RootState } from "integrations/redux"
import { useAppSelector } from "integrations/redux/hooks"
import { isEqual } from "nodash"
import { useEffect, useRef, useState } from "react"
import { Selector } from "react-redux"

export const useOptimisticLocalState = <T>(
    selector: (s: RootState) => T | undefined,
    callback: (value: T) => void,
    defaultValue: T,
    time = 0
) => {
    const reduxValue = useAppSelector(selector)
    const valueRef = useRef<T>(undefined)
    const [value, setValue] = useState<T>(reduxValue || defaultValue)

    const timeoutRef = useRef<NodeJS.Timeout>(undefined)

    const callbackRef = useRef(callback)
    useEffect(() => {
        callbackRef.current = callback
    }, [callback])

    const set = (value: T) => {
        setValue(value)
        valueRef.current = value

        if (timeoutRef.current) clearTimeout(timeoutRef.current)

        if (time) {
            timeoutRef.current = setTimeout(() => callbackRef.current(value), time)
        } else {
            callbackRef.current(value)
        }
    }

    useEffect(() => {
        if (!isEqual(reduxValue, valueRef.current)) {
            setValue(reduxValue || defaultValue)
            valueRef.current = reduxValue || defaultValue
        }
    }, [reduxValue, defaultValue])

    return [value, set] as [T, (value: T) => void]
}

export type OptimisticLocalStateParams<T> = {
    selector: Selector<RootState, T>
    updateFn: (t: T) => void
    defaultValue: T
}

export function useDefinedOptimisticLocalState<T>(params: OptimisticLocalStateParams<T>) {
    const [value, setValue] = useOptimisticLocalState<T>(
        params.selector,
        params.updateFn,
        params.defaultValue
    )
    return [value, setValue] as const
}
